import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import NewSelect from "../Inputs/NewSelect.jsx";
import NewText from "../Inputs/NewText.jsx";
import RadioGroupField from "../Inputs/RadioGroupField";
import MapComponent from "../Map/NewMap.jsx";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";
import "./styles/projectlocation.css";

// Map Instructions Component
const MapInstructions = () => {
  return (
    <div id="map-instructions">
      <h1 id="map-instructions-heading">Map & Pin Instructions</h1>
      <div className="instructions">
        <div className="instruction">
          <h3 className="instruction-name">Moving Around the Map</h3>
          <p className="map-instructions">
            To move around the map, click and drag the map or pressing the full
            screen button on the top right and press an arrow key in the
            direction you want to move.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Zoom</h3>
          <p className="map-instructions">
            To can zoom in and out of the map by scrolling up and down with your
            mouse or by pressing the plus and minus buttons on the bottom right
            of the map.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">
            Adding/Removing a Pin (Non-Linear)
          </h3>
          <p className="map-instructions">
            Click on the map where you would like to add the pin. Click on the
            pin to remove it from the map. You can move the pin by either
            clicking and removing the pin and then clicking again on the new
            location or by selecting a new area of the map to place the pin.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Adding/Removing a Pin (Linear)</h3>
          <p className="map-instructions">
            Click on the map where you would like to add the starting pin. Click
            again anywhere on the map to add the ending pin. Click either pin to
            remove it from the map. You can only move a specific pin by first
            clicking and removing the pin and then clicking again on the new
            location.
          </p>
        </div>
        <div className="instruction">
          <h3 className="instruction-name">Incorrect Address</h3>
          <p className="map-instructions">
            If the pin provides an incorrect address, please change the address
            in the field provided below the map.
          </p>
        </div>
      </div>
    </div>
  );
};

// Slide 5: Project Location
const ProjectLocation = ({ setSlideStatus, slideState, formStateLoaded }) => {
  const {
    slide1,
    slide5,
    setSlide5,
    setIsLoading,
    requestStatus,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
    disableAll,
  } = useContext(FormContext);

  const { clientId, projectId } = useContext(DashboardContext);

  const [showInstructions, setShowInstructions] = useState(false);
  const [disabledInputs, setDisabledInputs] = useState([]);
  const [counties, setCounties] = useState([
    "Apache",
    "Cochise",
    "Coconino",
    "Gila",
    "Graham",
    "Greenlee",
    "La Paz",
    "Maricopa",
    "Mohave",
    "Navajo",
    "Pima",
    "Pinal",
    "Santa Cruz",
    "Yavapai",
    "Yuma",
  ]);
  const [cities, setCities] = useState(
    [
      "Apache Junction",
      "Aguila",
      "Ajo",
      "Avondale",
      "Buckeye",
      "Carefree",
      "Casa Grande",
      "Cave Creek",
      "Chandler",
      "El Mirage",
      "Flagstaff",
      "Fountain Hills",
      "Glendale",
      "Gila Bend",
      "Gilbert",
      "Goodyear",
      "Guadalupe",
      "Litchfield Park",
      "Mesa",
      "Paradise Valley",
      "Peoria",
      "Phoenix",
      "Prescott",
      "Scottsdale",
      "Sun City",
      "Sun City West",
      "Surprise",
      "Queen Creek",
      "Tempe",
      "Tolleson",
      "Tucson",
      "Wickenburg",
      "Wikieup",
      "Youngtown",
      "Yuma",
    ].sort(),
  );

  useEffect(() => {
    scrollToFirstRequiredField(slide5);
  }, []);

  // Update Slide Logic
  const slide5Mutation = useSetSlide(
    clientId,
    projectId,
    5,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "5",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      <RadioGroupField
        label={"Is this a Linear Project?"}
        idProp={"isLinear"}
        val={slide5.isLinear}
        state={slide5}
        setState={setSlide5}
        debouncedSave={slide5Mutation}
        setIsLoading={setIsLoading}
        disableInput={disabledInputCheck("isLinear", disabledInputs)}
      />
      {showInstructions ? <MapInstructions /> : null}
      <header id="map-header">
        <h3
          id="map-heading"
          onClick={() => setShowInstructions(!showInstructions)}
        >
          {slide5.isLinear === "No" || slide5.isLinear === ""
            ? "Click to the center most point of the project"
            : "Click & Select the estimated beginning and ending points of the project"}
        </h3>
        <IconButton
          id="map-instructions-button"
          onClick={() => {
            setShowInstructions(!showInstructions);
          }}
        >
          <Tooltip
            id="rg-tooltip"
            title="Click to view/hide map instructions"
            classes={{
              tooltip: "radio-group-tooltip",
              popper: "radio-group-popper",
              arrow: "radio-group-arrow",
              tooltipPlacementRight: "radio-group-right",
            }}
            arrow
            placement="right"
          >
            <HelpOutlineIcon id="help-icon" />
          </Tooltip>
        </IconButton>
      </header>
      <MapComponent
        projectName={slide1.projectName}
        slide5={slide5}
        setSlide5={setSlide5}
        requestStatus={requestStatus}
        disableAll={disableAll}
        debouncedSave={slide5Mutation}
        setIsLoading={setIsLoading}
      />
      <div id="location-inputs">
        {!slide5.pAdd || !slide5.pCity || !slide5.pCounty || !slide5.pZip ? (
          <p className="important-text">
            Please Enter the Following Information
          </p>
        ) : null}
        <ResponsiveRow>
          <NewText
            label={"Closest Street Address"}
            idProp={"pAdd"}
            val={slide5.pAdd}
            state={slide5}
            setState={setSlide5}
            debouncedSave={slide5Mutation}
            setIsLoading={setIsLoading}
            req={slide5.pAdd === "" || slide5.pAdd.length < 10}
            type="address"
            disabledInput={disabledInputCheck("pAdd", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("pAdd", disabledInputs) === false
                ? getInputMessage("pAdd", invalidInputsData, "5")
                : ""
            }
            invalidData={getInvalidData("pAdd", invalidInputsData, "5")}
          />
          <NewText
            label={"City"}
            idProp={"pCity"}
            type="city"
            val={slide5.pCity}
            state={slide5}
            setState={setSlide5}
            debouncedSave={slide5Mutation}
            setIsLoading={setIsLoading}
            autoData={cities}
            req={slide5.pCity === "" || cities.includes(slide5.pCity) === false}
            disabledInput={disabledInputCheck("pCity", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("pCity", disabledInputs) === false
                ? getInputMessage("pCity", invalidInputsData, "5")
                : ""
            }
            invalidData={getInvalidData("pCity", invalidInputsData, "5")}
          />
          <NewText
            label={"Zip"}
            idProp={"pZip"}
            type="zipCode"
            val={slide5.pZip}
            state={slide5}
            setState={setSlide5}
            debouncedSave={slide5Mutation}
            setIsLoading={setIsLoading}
            req={
              slide5.pZip === "" ||
              slide5.pZip.length < 5 ||
              +slide5.pZip < 85001 ||
              +slide5.pZip > 86556
            }
            disabledInput={disabledInputCheck("pZip", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("pZip", disabledInputs) === false
                ? getInputMessage("pZip", invalidInputsData, "5")
                : ""
            }
            invalidData={getInvalidData("pZip", invalidInputsData, "5")}
          />
          <NewText
            label={"County"}
            idProp={"pCounty"}
            type="county"
            val={slide5.pCounty}
            state={slide5}
            setState={setSlide5}
            debouncedSave={slide5Mutation}
            setIsLoading={setIsLoading}
            autoData={counties}
            req={
              slide5.pCounty === "" ||
              counties.includes(slide5.pCounty) === false
            }
            disabledInput={disabledInputCheck("pCounty", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("pCounty", disabledInputs) === false
                ? getInputMessage("pCounty", invalidInputsData, "5")
                : ""
            }
            invalidData={getInvalidData("pCounty", invalidInputsData, "5")}
          />
        </ResponsiveRow>
      </div>
      <h2 className="question-section">Major Cross Roads</h2>
      <ResponsiveRow type="center">
        <NewText
          label={"Cross Road 1"}
          idProp={"crossRoad1"}
          val={slide5.crossRoad1}
          state={slide5}
          setState={setSlide5}
          debouncedSave={slide5Mutation}
          setIsLoading={setIsLoading}
          req={false}
          disabledInput={disabledInputCheck("crossRoad1", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("crossRoad1", disabledInputs) === false
              ? getInputMessage("crossRoad1", invalidInputsData, "5")
              : ""
          }
          invalidData={getInvalidData("crossRoad1", invalidInputsData, "5")}
          type="city"
        />
        <NewText
          label={"Cross Road 2"}
          idProp={"crossRoad2"}
          val={slide5.crossRoad2}
          state={slide5}
          setState={setSlide5}
          debouncedSave={slide5Mutation}
          setIsLoading={setIsLoading}
          req={false}
          disabledInput={disabledInputCheck("crossRoad2", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("crossRoad2", disabledInputs) === false
              ? getInputMessage("crossRoad2", invalidInputsData, "5")
              : ""
          }
          invalidData={getInvalidData("crossRoad2", invalidInputsData, "5")}
          type="city"
        />
        <NewSelect
          idProp="cornerDirection"
          label="Direction"
          type={"cornerDirection"}
          val={slide5.cornerDirection}
          state={slide5}
          setState={setSlide5}
          debouncedSave={slide5Mutation}
          setIsLoading={setIsLoading}
          req={false}
          disabledInput={disabledInputCheck("cornerDirection", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("cornerDirection", disabledInputs) === false
              ? getInputMessage("cornerDirection", invalidInputsData, "5")
              : ""
          }
          invalidData={getInvalidData(
            "cornerDirection",
            invalidInputsData,
            "5",
          )}
        />
      </ResponsiveRow>
    </>
  );
};

export default ProjectLocation;
